import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import EducationalDisclaimer from '@/components/feature/EducationalDisclaimer';

const NevadaDuplicateTitlePage: React.FC = () => (
  <div className="min-h-screen bg-white">
    <Header />
    <section className="pt-28 pb-12 bg-obsidian-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center space-x-2 text-sm text-obsidian-400 mb-4">
          <Link to="/" className="hover:text-gold-400">Home</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/nevada-dmv-info" className="hover:text-gold-400">DMV Info</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gold-400">Duplicate Title</span>
        </div>
        <h1 className="font-display text-4xl font-bold text-white mb-3">Nevada Duplicate Vehicle Title</h1>
        <p className="text-obsidian-300">Lost or damaged your Nevada vehicle title? Here&apos;s how to get a duplicate title.</p>
      </div>
    </section>
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <EducationalDisclaimer />
      <div className="space-y-8 text-obsidian-700">
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">When You Need a Duplicate Title</h2>
          <p>You need to apply for a duplicate Nevada title if your original title has been lost, stolen, mutilated, or destroyed. You cannot sell or transfer a vehicle without a valid title, so getting a duplicate is essential.</p>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Required Forms</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { form: 'VP 012', name: 'Application for Duplicate Certificate of Title', desc: 'Primary form for requesting a duplicate title. Available at dmv.nv.gov or any DMV office.' },
              { form: 'VP 206', name: 'Affidavit of Lost Title', desc: 'Often required alongside VP 012 to certify the original title is lost or destroyed.' },
            ].map(item => (
              <div key={item.form} className="border border-obsidian-100 rounded-lg p-5">
                <span className="inline-block bg-obsidian-900 text-gold-400 text-xs font-bold px-2.5 py-1 rounded font-mono mb-2">{item.form}</span>
                <p className="font-semibold text-obsidian-900 text-sm mb-1">{item.name}</p>
                <p className="text-obsidian-500 text-xs">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Fees & Processing Time</h2>
          <div className="bg-obsidian-50 rounded-xl p-6">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-obsidian-200"><th className="text-left py-2 font-semibold text-obsidian-900">Item</th><th className="text-right py-2 font-semibold text-obsidian-900">Details</th></tr></thead>
              <tbody className="divide-y divide-obsidian-100">
                {[['Duplicate Title Fee', '$20.00'], ['Processing Time (mail)', 'Approximately 6 weeks'], ['Expedited Processing', 'Available for additional fee (Form VP 265)'], ['Delivery', 'Mailed to Nevada address on file']].map(([l, a]) => (
                  <tr key={l}><td className="py-2.5 text-obsidian-700">{l}</td><td className="py-2.5 text-right font-medium text-obsidian-900">{a}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">If There Is a Lien on the Vehicle</h2>
          <p className="text-sm">If your vehicle has an active lien (loan), the lienholder may need to be involved in the duplicate title process. The duplicate title will be sent to the lienholder if one is listed on the title. Contact your lender for guidance.</p>
        </section>
        <div className="bg-gold-50 border border-gold-200 rounded-xl p-6 text-center">
          <h3 className="font-display font-bold text-obsidian-900 text-lg mb-2">Need a Duplicate Title?</h3>
          <p className="text-obsidian-600 text-sm mb-4">Speedy DMV Services handles duplicate title applications. We ensure all paperwork is correct for faster processing.</p>
          <Link to="/appointment" className="inline-block bg-gold-gradient text-obsidian-900 px-6 py-2.5 rounded-lg font-bold text-sm hover:brightness-110 transition-all whitespace-nowrap">Book Appointment</Link>
        </div>
      </div>
    </div>
    <Footer />
  </div>
);

export default NevadaDuplicateTitlePage;
